import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { collection, getDocs, orderBy, query, where } from "firebase/firestore";
import { db } from "../firebase";
import PublicNavigation from "../components/PublicNavigation";
import "./AvailableProjects.css";

export default function AvailableProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filter, setFilter] = useState("all");
  const [districtFilter, setDistrictFilter] = useState("all");

  // ✅ NEW: view mode toggle (cards | table)
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("projectsViewMode") || "cards");
  useEffect(() => localStorage.setItem("projectsViewMode", viewMode), [viewMode]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const q = query(
          collection(db, "projects"),
          where("status", "==", "available"),
          orderBy("createdAt", "desc")
        );
        const snap = await getDocs(q);
        setProjects(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error("Error fetching projects:", e);
        setProjects([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(
    () => [...new Set(projects.map((p) => p.category).filter(Boolean))].sort(),
    [projects]
  );
  const districts = useMemo(
    () => [...new Set(projects.map((p) => p.location).filter(Boolean))].sort(),
    [projects]
  );

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const okCat = filter === "all" || p.category === filter;
      const okDist = districtFilter === "all" || p.location === districtFilter;
      return okCat && okDist;
    });
  }, [projects, filter, districtFilter]);

  return (
    <div className="projects-container">
      <PublicNavigation />

      <div className="projects-content">
        <div className="projects-header">
          <h1>Available Projects</h1>

          {/* ✅ NEW: toggle buttons */}
          <div className="view-toggle">
            <button
              type="button"
              className={viewMode === "cards" ? "active" : ""}
              onClick={() => setViewMode("cards")}
            >
              Card View
            </button>
            <button
              type="button"
              className={viewMode === "table" ? "active" : ""}
              onClick={() => setViewMode("table")}
            >
              Table View
            </button>
          </div>

          <p>Browse community service projects and express your interest</p>
        </div>

        <div className="filter-section">
          <div className="filter-group">
            <label>Filter by Category:</label>
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Filter by District:</label>
            <select value={districtFilter} onChange={(e) => setDistrictFilter(e.target.value)}>
              <option value="all">All Districts</option>
              {districts.map((dist) => (
                <option key={dist} value={dist}>
                  {dist}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading">Loading projects...</div>
        ) : filteredProjects.length === 0 ? (
          <div className="no-projects">
            <p>No available projects at this time. Check back soon!</p>
          </div>
        ) : viewMode === "cards" ? (
          <div className="projects-grid">
            {filteredProjects.map((project) => (
              <Link to={`/projects/${project.id}`} key={project.id} className="project-card">
                <div className="project-category">{project.category}</div>
                <h3>{project.title}</h3>
                <p className="project-location">📍 {project.location}</p>
                <p className="project-description">
                  {(project.description || "").substring(0, 120)}
                  {(project.description || "").length > 120 ? "..." : ""}
                </p>
                <div className="project-meta">
                  <span>💰 ${project.budget?.toLocaleString?.() || "TBD"}</span>
                  <span className="view-details">View Details →</span>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="projects-table-wrap">
            <table className="projects-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>District</th>
                  <th>Budget</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredProjects.map((project) => (
                  <tr key={project.id}>
                    <td style={{ fontWeight: 600 }}>{project.title}</td>
                    <td>{project.category || "-"}</td>
                    <td>{project.location || "-"}</td>
                    <td>${project.budget?.toLocaleString?.() || "TBD"}</td>
                    <td style={{ whiteSpace: "nowrap" }}>
                      <Link className="table-link" to={`/projects/${project.id}`}>
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
